import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva } from "class-variance-authority";
import { Button } from "@/components/ui/buttons";
import { cn } from "@/lib/utils";
import type { ButtonConfig, ArrowDirection, ThemeVariant } from "@/types";
import { Arrow } from "@/components/ui/Arrow";
import { Wysiwyg } from "../ui/Wysiwyg";
import { HeadingTag, HeadingStyle, WysiwygHeading } from "../ui/Heading";
const textBlockContentVariants = cva("", {
  variants: {
    variant: {
      light: "text-white childLinkLight",
      dark: "text-black childLinkDark"
    }
  },
  defaultVariants: {
    variant: "light"
  }
});
const toplineVariants = cva("text-lg uppercase", {
  variants: {
    variant: {
      light: "",
      dark: "text-gray-dark"
    }
  },
  defaultVariants: {
    variant: "light"
  }
});
export interface TextBlockContentProps {
  variant?: ThemeVariant;
  topline?: string;
  headline?: string;
  subline?: string;
  text?: string;
  headingTag?: HeadingTag;
  headingStyle?: HeadingStyle;
  className?: string;
  button?: ButtonConfig;
}

/**
 * TextBlockContent component - reusable text content without grid/layout concerns
 * Used by both TextBlock and ContentCardTeaser
 */
export const TextBlockContent: React.FC<TextBlockContentProps> = t0 => {
  const $ = _c(25);
  const {
    topline,
    headline,
    subline,
    text,
    headingTag: t1,
    headingStyle: t2,
    variant: t3,
    className: t4,
    button
  } = t0;
  const TagComponent = t1 === undefined ? "h2" : t1;
  const headingStyle = t2 === undefined ? "h2" : t2;
  const variant = t3 === undefined ? "light" : t3;
  const className = t4 === undefined ? "" : t4;
  let t5;
  if ($[0] !== className || $[1] !== variant) {
    t5 = textBlockContentVariants({
      variant,
      className
    });
    $[0] = className;
    $[1] = variant;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== topline || $[4] !== variant) {
    t6 = topline && <p className={cn(toplineVariants({
      variant
    }), "mb-xs")}>{topline}</p>;
    $[3] = topline;
    $[4] = variant;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== TagComponent || $[7] !== headingStyle || $[8] !== headline) {
    t7 = headline && <WysiwygHeading tag={TagComponent} style={headingStyle} className="mb-md" html={headline} />;
    $[6] = TagComponent;
    $[7] = headingStyle;
    $[8] = headline;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== subline || $[11] !== variant) {
    t8 = subline && <p className={cn(toplineVariants({
      variant
    }), "-mt-sm mb-md")}>{subline}</p>;
    $[10] = subline;
    $[11] = variant;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== text) {
    t9 = text && <Wysiwyg html={text} mode="body" />;
    $[13] = text;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== button || $[16] !== variant) {
    t10 = button && <div className={cn("mt-8 flex", button.position === "center" ? "justify-center" : "justify-start")}><Button label={button.label} icon={button.icon} link={button.link} variant={button.variant} color={button.color ?? (variant === "light" ? "yellow" : "purple")} /></div>;
    $[15] = button;
    $[16] = variant;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = <div data-component="text-block-content" className={t5}>{t6}{t7}{t8}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};
export interface TextBlockProps extends TextBlockContentProps {
  arrowDirection?: ArrowDirection;
}

/**
 * TextBlock component - text content with optional arrow in grid layout
 */
export const TextBlock: React.FC<TextBlockProps> = t0 => {
  const $ = _c(16);
  const {
    topline,
    headline,
    subline,
    text,
    headingTag: t1,
    headingStyle: t2,
    variant: t3,
    arrowDirection,
    className: t4,
    button
  } = t0;
  const headingTag = t1 === undefined ? "h2" : t1;
  const headingStyle = t2 === undefined ? "h2" : t2;
  const variant = t3 === undefined ? "light" : t3;
  const className = t4 === undefined ? "" : t4;
  let t5;
  if ($[0] !== arrowDirection || $[1] !== variant) {
    t5 = arrowDirection && <div className="col-span-3 hidden md:block"><div className="h-full flex items-center justify-center"><Arrow direction={arrowDirection} variant="large" color={variant === "light" ? "yellow" : "purple"} className="" /></div></div>;
    $[0] = arrowDirection;
    $[1] = variant;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== button || $[4] !== className || $[5] !== headingStyle || $[6] !== headingTag || $[7] !== headline || $[8] !== subline || $[9] !== text || $[10] !== topline || $[11] !== variant) {
    t6 = <div className="md:col-span-9 col-span-12"><TextBlockContent topline={topline} headline={headline} subline={subline} text={text} headingTag={headingTag} headingStyle={headingStyle} variant={variant} button={button} className={className} /></div>;
    $[3] = button;
    $[4] = className;
    $[5] = headingStyle;
    $[6] = headingTag;
    $[7] = headline;
    $[8] = subline;
    $[9] = text;
    $[10] = topline;
    $[11] = variant;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="container">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};